import {
    forwardRef,
    ForwardedRef,
    ReactNode,
    useRef,
    useImperativeHandle,
} from 'react';
import cn from 'classnames';
import styles from './index.module.less';
import site from 'pro/config/site';
import { Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import {
    Navigation,
    Pagination,
    EffectFade,
    Autoplay,
    Zoom,
    Scrollbar,
} from 'swiper/modules';
import { Iconfont } from '@base/lib/Iconfont';
import { useSwiperRef } from './useSwiperRef';
import { IconArrow } from '@base/icon/IconArrow';

export type IThemeSwiperProps = SwiperProps & {
    list?: Array<any>;
    render?: (item: any, index: number) => ReactNode;
    renderSlideProps?: (item: any, index: number) => Record<string, any>;
    navigationHideWhenDisabled?: boolean;
    'data-t-mod'?: string;
    extraRender?: {
        afterList?: ReactNode;
        preList?: ReactNode;
    },
    iconPrev?: ReactNode;
    iconNext?: ReactNode;
    swiperClassName?: string;
}

export type IThemeSwiperRef = {
    getContainerEl: () => HTMLDivElement;
    getSwiperEl: () => HTMLElement;
    slideTo: (index: number, duation?: number) => void;
    slideToLoop: (index: number, duation?: number) => void;
    startAutoplay: () => void;
    stopAutoplay: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    zoomToggle: () => void;
};

function WrapSwiper(props: IThemeSwiperProps, ref: ForwardedRef<IThemeSwiperRef>) {
    const {
        list = [],
        render,
        renderSlideProps,
        className,
        swiperClassName,
        'data-t-mod': dataTrackModule,
        navigation: _navigation = false,
        navigationHideWhenDisabled = false,
        pagination: _pagination = false,
        loop = false,
        autoplay,
        slidesPerView,
        zoom = false,
        scrollbar = false,  // {hide: true} 滚动时显示滚动条
        extraRender,
        iconPrev = 'iconfont' in site ? <Iconfont className="icon-arrow-left"/> : <IconArrow direction={'left'}/>,
        iconNext = 'iconfont' in site ? <Iconfont className="icon-arrow-right"/> : <IconArrow direction={'right'}/>,
        ...rest
    } = props;

    const swiperRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [prevEl, prevRef] = useSwiperRef<HTMLDivElement>();
    const [nextEl, nextRef] = useSwiperRef<HTMLDivElement>();

    const isSingle = list.length < 2 || (typeof slidesPerView === 'number' && list.length <= slidesPerView);
    const navigation = isSingle || !_navigation
        ? false
        : {
            prevEl,
            nextEl,
            disabledClass: navigationHideWhenDisabled ? 'hidden' : 'disabled',
            ...(_navigation === true ? {} : _navigation)
        };
    const pagination = isSingle || !_pagination
        ? false
        : (_pagination === true ? { clickable: true, disableOnInteraction: false } : { ..._pagination, disableOnInteraction: false });

    const getSwiper = () => swiperRef.current as SwiperClass;

    useImperativeHandle(ref, () => ({
        getContainerEl: () => containerRef.current as HTMLDivElement,
        getSwiperEl: () => getSwiper()?.el,
        slideTo: (index: number, duation?: number) => getSwiper()?.slideTo(index, duation),
        slideToLoop: (index: number, duation?: number) => getSwiper()?.slideToLoop(index, duation),
        startAutoplay: () => getSwiper()?.autoplay.start(),
        stopAutoplay: () => getSwiper()?.autoplay.stop(),
        zoomIn: () => getSwiper()?.zoom.in(),
        zoomOut: () => getSwiper()?.zoom.out(),
        zoomToggle: () => getSwiper()?.zoom.toggle(),
    }));

    return list.length === 0 ? null : (
        <div
            className={cn(className, styles.swiper)}
            ref={containerRef}
        >
            <Swiper
                className={cn(swiperClassName, 'g-swiper')}
                data-t-mod={dataTrackModule}
                navigation={navigation}
                pagination={pagination}
                loop={isSingle ? false : loop}
                autoplay={autoplay}
                slidesPerView={slidesPerView}
                zoom={zoom === true ? { toggle: false } : zoom}
                onSwiper={swiper => swiperRef.current = swiper}
                scrollbar={scrollbar}
                modules={[
                    Navigation,
                    Pagination,
                    EffectFade,
                    Autoplay,
                    Zoom,
                    Scrollbar
                ]}
                {...rest}
            >
                {extraRender?.preList && (
                    <SwiperSlide key={'preList'} className={'swiper-slide-auto'}>
                        {extraRender.preList}
                    </SwiperSlide>
                )}

                {list.map((item, index) => {
                    let { className, ...rest } = renderSlideProps?.(item, index) || {};
                    if (slidesPerView === 'auto') {
                        className = cn(className, 'swiper-slide-auto');
                    }
                    return (
                        <SwiperSlide
                            key={index}
                            className={cn(className, 'g-swiper-slide')}
                            {...rest}
                        >
                            {render?.(item, index)}
                        </SwiperSlide>
                    )
                })}

                {extraRender?.afterList && (
                    <SwiperSlide key={'afterList'} className={'swiper-slide-auto'}>
                        {extraRender.afterList}
                    </SwiperSlide>
                )}
            </Swiper>

            {navigation !== false && (
                <div className={'g-navigation'}>
                    <div ref={prevRef} className="g-prev">
                        {iconPrev}
                    </div>
                    <div ref={nextRef} className="g-next">
                        {iconNext}
                    </div>
                </div>
            )}
        </div>
    );
}

export const ThemeSwiper = forwardRef(WrapSwiper);
